import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { FormInput, LabeledStatus } from 'components/molecules';
import LabeledArtistType from 'components/molecules/labeledStatus/LabeledArtistType';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible, useHasPermission } from 'hooks';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useTranslation } from 'react-i18next';
import { PiCheck, PiUserPlus } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useAcceptArtist, useGetArtistListInfinite } from 'services/artists';
import { useGetLicenceConfiguration } from 'services/settings/individual';
import {
  IArtistExternalIdMutation,
  IArtistResponse,
} from 'services/artists/types';
import { STATUS } from 'services/types';
import { ARTIST_TYPE } from 'types/artist/artist.interface';
import { IArtistSearch } from 'types/artist/search.interface';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { magellanColumns } from 'types/components/table/table.types';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { getArtistName } from 'utils/global/name.utils';
import { getNodes } from 'utils/table/nodes.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { useSearchById, useSetSearchById } from 'store/state';

enum ARTIST_COLUMNS {
  isBand,
  nickname,
  name,
  contact,
  status,
  externalProfiles,
  // type,
}

const artistsSearchInitialValues: IArtistSearch = {
  EAN: '',
  email: '',
  externalId: '',
  externalIdType: '',
  firstName: '',
  ISRC: '',
  lastName: '',
  nickname: '',
  phoneNumber: '',
  status: undefined,
};

const Artists = () => {
  usePermissionGuard('ArtistPermissionsLIST**');
  const hasPermission = useHasPermission();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const columnsVisibility = useTableColumnsVisibility(
    'Artists2',
    ARTIST_COLUMNS
  );

  const i18nextPrefix = 'artist';
  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false, true, columnsVisibility.tableId);

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters = searchById[columnsVisibility.tableId] || artistsSearchInitialValues;

  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useGetArtistListInfinite({ ...filters, itemCount: 25 });

  const acceptArtist = useAcceptArtist();
  const licenceConfiguration = useGetLicenceConfiguration();

  const tableData = getNodes<IArtistResponse>(data?.pages.flat());

  const isClientModuleEnabled =
    licenceConfiguration.data?.clientModuleAccess ?? false;

  const handleAcceptArtist = async (item: IArtistResponse) => {
    await acceptArtist.mutateAsync(
      { artistId: item.id, version: item.version },
      {
        onSuccess: async () => {
          await refetch();
        },
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const { getSelectOptions } = useEnumToSelectOptions();

  const hasDetailPermission = hasPermission('ArtistPermissionsLIST**', 128);

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }
    
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/external`, {
      relative: 'path',
    });
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const tableSelect = hasDetailPermission ? select : undefined;

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          path=''
          hideBackButton
          topbarContent={
            <>
              <div className='flex'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  filters={filters}
                  isClear
                />
                <Table.CustomColumnsButton
                  columns={ARTIST_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix={i18nextPrefix}
                />
              </div>
              {hasPermission('ArtistPermissionsLIST**', 1) && (
                <Button
                  variant={BUTTON_VARIANT.submit}
                  onClick={() =>
                    navigate('create/external', { replace: false })
                  }>
                  <Button.Text
                    text={t('button.create-what', { what: t('artist.what') })}
                  />
                  <Button.Icon icon={<PiUserPlus />} />
                </Button>
              )}
            </>
          }>
          <QueryFilter<IArtistSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IArtistSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values => setSearchById(columnsVisibility.tableId, values)}>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='status'
                    label={t('input.label.status')}
                    options={getSelectOptions(STATUS, 'enum.STATUS')}
                  />
                  <FormInput.Text
                    name='firstName'
                    label={t('input.label.firstName')}
                  />
                  <FormInput.Text
                    name='lastName'
                    label={t('input.label.lastName')}
                  />
                  <FormInput.Text
                    name='nickname'
                    label={t('input.label.nickname')}
                  />
                  <FormInput.Text
                    name='email'
                    label='Email'
                    type='email'
                    placeholder='example@mail.com'
                  />
                  <FormInput.Mask
                    name='phoneNumber'
                    label={t('input.label.phoneNumber')}
                    mask='+(##) ### ### ###'
                    placeholder='+(01) 123 456 789'
                  />
                  <FormInput.Text
                    name='externalIdType'
                    label={t('input.label.externalIdType')}
                    placeholder='Spotify'
                  />
                  <FormInput.Mask
                    name='externalId'
                    label={t('input.label.externalId')}
                    mask='________-____-____-____-____________'
                    placeholder='3b716a69-4840-45b5-90f8-c4b05fdf83f9'
                  />
                  <FormInput.Text
                    name='isrc'
                    label='ISRC'
                  />
                  <FormInput.Text
                    name='ean'
                    label={t('input.label.ean')}
                    placeholder='1234567890'
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data =>
                  data.key === 'status'
                    ? t(`enum.STATUS.${STATUS[data.value as STATUS]}`)
                    : `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        select={tableSelect}
        columns={magellanColumns<IArtistResponse>([
          {
            label: `${t(
              `${i18nextPrefix}.table.${ARTIST_COLUMNS[ARTIST_COLUMNS.isBand]}`
            )}`,
            renderCell: item => (
              <TableItem>
                <LabeledArtistType
                  status={item.isBand ? ARTIST_TYPE.true : ARTIST_TYPE.false}
                />
              </TableItem>
            ),
            hide: columnsVisibility.hide(ARTIST_COLUMNS.isBand),
            resize: true,
          },
          {
            label: `${t(
              `${i18nextPrefix}.table.${
                ARTIST_COLUMNS[ARTIST_COLUMNS.nickname]
              }`
            )}`,
            renderCell: item => (
              <TableItem>
                <div className='flex flex-row items-center gap-4'>
                  <TableItem.Image
                    url={item.profilePicturePreviewLink}
                    alt={item.nickname}
                  />
                  <TableItem.Text text={item.nickname} />
                </div>
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(ARTIST_COLUMNS.nickname),
            minWidth: 200,
          },
          {
            label: t(
              `${i18nextPrefix}.table.${ARTIST_COLUMNS[ARTIST_COLUMNS.name]}`
            ),
            renderCell: item => (
              <TableItem>
                <TableItem.Text text={getArtistName(item.name)} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(ARTIST_COLUMNS.name),
          },

          {
            label: t(
              `${i18nextPrefix}.table.${ARTIST_COLUMNS[ARTIST_COLUMNS.contact]}`
            ),
            renderCell: ({ phoneNumber, email }) => (
              <TableItem className='gap-2'>
                <TableItem.Text text={email} />
                {phoneNumber ? (
                  <TableItem.Text
                    text={`${phoneNumber.prefix} ${phoneNumber.number}`}
                    isSupplementary
                  />
                ) : null}
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(ARTIST_COLUMNS.contact),
          },

          {
            label: t(
              `${i18nextPrefix}.table.${
                ARTIST_COLUMNS[ARTIST_COLUMNS.externalProfiles]
              }`
            ),
            renderCell: item => (
              <TableItem>
                <TableItem.List<IArtistExternalIdMutation>
                  items={item.externalIds}>
                  {external => (
                    <TableItem.Text
                      isSupplementary
                      key={external.id}
                      text={`${external.type}`}
                    />
                  )}
                </TableItem.List>
              </TableItem>
            ),
            hide: columnsVisibility.hide(ARTIST_COLUMNS.externalProfiles),
            resize: true,
          },
          {
            label: t(
              `${i18nextPrefix}.table.${ARTIST_COLUMNS[ARTIST_COLUMNS.status]}`
            ),
            renderCell: item => (
              <TableItem justifyPosition='center'>
                <LabeledStatus status={item.artistStatus} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(ARTIST_COLUMNS.status),
            minWidth: 140,
          },
          {
            label: '',
            minWidth: 56,
            renderCell: item => (
              <TableItem justifyPosition='end'>
                {isClientModuleEnabled &&
                  item.artistStatus === STATUS.inVerification && (
                  <Button
                    onlyIcon
                    size='compact'
                    variant={BUTTON_VARIANT.submit}
                    isLoading={
                      acceptArtist.isPending &&
                      acceptArtist.variables?.artistId === item.id
                    }
                    disabled={
                      acceptArtist.isPending &&
                      acceptArtist.variables?.artistId === item.id
                    }
                    onClick={event => {
                      event.stopPropagation();
                      handleAcceptArtist(item);
                    }}>
                    <Button.Description description={t('button.accept')} />

                    <Button.Icon icon={<PiCheck />} />
                  </Button>
                )}
              </TableItem>
            ),
            resize: true,
          },
        ])}
        displayMode
        loading={isLoading || isFetchingNextPage}
        data={tableData}
        onFullyScroll={fetchNextPage}
      />
    </TopbarPage>
  );
};

export default Artists;
